/* eslint-disable react/jsx-sort-props */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import makeStyles from '@mui/styles/makeStyles';
import type { Theme } from '@mui/material/styles';

import InputTextField from '@design-system/TextField';
import ButtonPrimary from '@design-system/ButtonPrimary';
import ButtonOutlined from '@design-system/ButtonOutlined';
import ButtonLink from '@design-system/ButtonLink';

import { useSatProductKeySearch, SAT_SEARCH_MIN_CHARS } from 'hooks/invoicing/useSatProductKeySearch';

export interface SatProductKeyValue {
  code: string;
  description: string;
}

interface Props {
  /** Currently selected `{code, description}` or null when nothing has been assigned. */
  value: SatProductKeyValue | null;
  /** Fires with the picked option, or with the default value when the user clicks "Restablecer". */
  onChange: (next: SatProductKeyValue | null) => void;
  /** Label shown above the read-only display. */
  label: string;
  /**
   * Default SAT key the field falls back to when the user clicks "Restablecer". Optional —
   * when omitted the restore link is not rendered.
   */
  defaultValue?: SatProductKeyValue;
}

const useStyles = makeStyles((theme: Theme) => ({
  // The outer wrapper needs to be a block constrained to its parent's width.
  // Inside a Grid item the picker would otherwise grow to fit the display Box's
  // longest content (the saved "<code> - <description>") and overflow the
  // drawer — same horizontal-scroll bug other fields don't hit because they're
  // simple TextFields rendered at width:100% by default.
  root: {
    width: '100%',
    minWidth: 0,
  },
  label: {
    fontSize: 14,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightRegular,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    // min-width: 0 lets the flex display Box actually shrink past its content
    // width so the ellipsis below kicks in; without it the row would push past
    // the parent and trigger the drawer scrollbar.
    minWidth: 0,
  },
  display: {
    flex: 1,
    minWidth: 0,
    // Match ButtonOutlined's height (42px) so the read-only field and the
    // "Cambiar" button line up at the same height in the row.
    height: theme.typography.pxToRem(42),
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    border: `1px solid ${theme.palette.darkGrey[15]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.darkGrey[5],
    fontSize: 14,
    color: theme.palette.common.black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  placeholder: {
    color: theme.palette.darkGrey[50],
  },
  resetRow: {
    marginTop: theme.spacing(1),
  },
  // Applied to the Dialog's own paper surface (via PaperProps) — no nested Paper.
  // A nested <Paper> with its own maxWidth left a strip of the Dialog's paper
  // showing on the side; styling the Dialog paper directly avoids the double
  // container entirely.
  dialogPaper: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(20),
    margin: 0,
  },
  helper: {
    color: theme.palette.darkGrey.main,
    fontSize: 14,
  },
  resultsList: {
    maxHeight: 280,
    overflowY: 'auto',
    border: `1px solid ${theme.palette.darkGrey[15]}`,
    borderRadius: theme.shape.borderRadius,
    padding: 0,
    margin: 0,
    listStyle: 'none',
  },
  resultItem: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    fontSize: 14,
    borderBottom: `1px solid ${theme.palette.darkGrey[5]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette.darkGrey[5],
    },
  },
  empty: {
    color: theme.palette.darkGrey.main,
    fontSize: 14,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  loadingRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
}));

/**
 * Read-only display + edit-dialog UX for the SAT product-key field. We avoid the
 * controlled-MUI-Autocomplete dance entirely: the visible field is a styled Box
 * that mirrors the saved code+description, and editing is a discrete action via
 * the "Cambiar"/"Asignar" button. Inside the dialog the user types a query, picks
 * one of the listed results, and the picker commits a single `{code, description}`
 * value back to the parent — no race conditions, no Autocomplete equality
 * warnings, no input syncing surprises.
 */
function SatProductKeyPicker({ value, onChange, label, defaultValue }: Props) {
  const classes = useStyles();
  const { t } = useTranslation('menuMaker');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { options, isLoading } = useSatProductKeySearch(query);

  // Reset the search state every time the dialog closes so the next open starts
  // fresh — leaving the previous query around would feel surprising and might
  // even show stale results if the saved value changed in the meantime.
  useEffect(() => {
    if (!dialogOpen) setQuery('');
  }, [dialogOpen]);

  const displayValue = useMemo(() => {
    if (!value?.code) return '';
    return value.description ? `${value.code} - ${value.description}` : value.code;
  }, [value]);

  const trimmedQuery = query.trim();
  const tooShort = trimmedQuery.length < SAT_SEARCH_MIN_CHARS;
  const showReset = Boolean(defaultValue && value?.code && value.code !== defaultValue.code);

  function handlePick(option: SatProductKeyValue) {
    onChange(option);
    setDialogOpen(false);
  }

  function handleReset() {
    if (defaultValue) onChange(defaultValue);
  }

  return (
    <>
      <Box className={classes.root}>
        <Typography className={classes.label} component="span">
          {label}
        </Typography>
        <Box className={classes.fieldRow}>
          <Box className={classes.display} data-testid="satProductKeyDisplay" title={displayValue}>
            {displayValue || <span className={classes.placeholder}>{t('menuMaker:productForm.satPicker.empty')}</span>}
          </Box>
          <ButtonOutlined data-testid="satProductKeyOpenDialog" onClick={() => setDialogOpen(true)}>
            {value?.code ? t('menuMaker:productForm.satPicker.change') : t('menuMaker:productForm.satPicker.assign')}
          </ButtonOutlined>
        </Box>
        {showReset && (
          <Box className={classes.resetRow}>
            <ButtonLink data-testid="satProductKeyReset" onClick={handleReset}>
              {t('menuMaker:productForm.satPicker.reset')}
            </ButtonLink>
          </Box>
        )}
      </Box>

      <Dialog
        PaperProps={{ className: classes.dialogPaper }}
        data-testid="satProductKeyDialog"
        fullWidth
        maxWidth="sm"
        onClose={() => setDialogOpen(false)}
        open={dialogOpen}
      >
        <Typography className={classes.title} component="h3">
          {t('menuMaker:productForm.satPicker.dialogTitle')}
        </Typography>
        <Typography className={classes.helper}>{t('menuMaker:productForm.satPicker.dialogHelper')}</Typography>

        <InputTextField
          autoFocus
          data-testid="satProductKeyDialogSearch"
          fullWidth
          label={t('menuMaker:productForm.satPicker.searchLabel')}
          name="satProductKeyDialogSearch"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          placeholder={t('menuMaker:productForm.satSearchPlaceholder')}
          value={query}
        />

        {tooShort && <Box className={classes.empty}>{t('menuMaker:productForm.satSearchMinChars')}</Box>}

        {!tooShort && isLoading && (
          <Box className={classes.loadingRow}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!tooShort && !isLoading && options.length === 0 && (
          <Box className={classes.empty}>{t('menuMaker:productForm.satSearchNoOption')}</Box>
        )}

        {!tooShort && options.length > 0 && (
          <ul className={classes.resultsList} data-testid="satProductKeyDialogResults">
            {options.map((option) => (
              <li
                className={classes.resultItem}
                data-testid={`satProductKeyOption-${option.code}`}
                key={option.code}
                onClick={() => handlePick(option)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handlePick(option);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <strong>{option.code}</strong> — {option.description}
              </li>
            ))}
          </ul>
        )}

        <Box className={classes.actions}>
          <ButtonPrimary onClick={() => setDialogOpen(false)}>{t('common:buttons.cancel')}</ButtonPrimary>
        </Box>
      </Dialog>
    </>
  );
}

export default SatProductKeyPicker;
